import { useEffect, useState, useMemo, useRef, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Tooltip from '@mui/material/Tooltip';
import { PageTitle } from '../../../../../_metronic/layout/core';
import { GetPermissions } from '../../../../modules/auth/core/Auth';
import { isBriefingV4Enabled } from '../../shared/utils/briefingTemplateVersion';
import type { XlsxExporter } from '../../shared/utils/xlsxExport';
import {
  Search,
  Filter,
  Grid3X3,
  List,
  CalendarDays,
  User,
  Eye,
  FileSpreadsheet,
  RefreshCw,
  Tag,
  TrendingUp,
  Gift,
  ListChecks,
  BadgePercent,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  X,
  CircleDollarSign,
  CloudUpload,
  CheckCircle2,
  AlertCircle,
  LoaderCircle,
  Ban,
} from 'lucide-react';

import type { HistoryRecord, HistoryStatus, PipefyStatus } from '../data/mockHistory';
import { STATUS_LABELS } from '../data/mockHistory';
import { useTemplates } from '../../promotions/data/templates';
import type { FormValues, TemplateDef } from '../../promotions/types';
import {
  exportRebaixaSimples,
  exportRebaixaProgressiva,
  exportCompreEGanhe,
  exportListaSugeridaOriginal,
  exportListaSugeridaRecompra,
  exportCupomSimples,
  exportCupomProgressivo,
  exportCashback,
  exportRebaixaCupomGeral,
  exportBriefingsByTemplate,
  createBriefingsXlsxFile,
} from '../../shared/utils/xlsxExport';
import {
  cancelBriefingsPipefy,
  enqueueBriefingsPipefyImport,
  fetchAllBriefingPromocoes,
  fetchBriefingPromocao,
} from '../../promotions/services/briefingPromocoesApi';
import HistoryDetailDrawer from '../components/HistoryDetailDrawer';
import CancelPipefyCardsModal from '../components/CancelPipefyCardsModal';
import {
  getSellerOptionsFromTemplate,
  normalizeSellerFieldsInValues,
} from '../../shared/utils/sellerValues';
import {
  PIPEFY_PAST_START_DATE_MESSAGE,
  isPromotionStartDateInPast,
} from '../utils/pipefyEligibility';

import {
  PIPEFY_FAILURE_CANCELLATION_MESSAGES, hasPendingPipefyFailureCancellation, requiresManualPipefyCancellation,
} from '../utils/pipefyFailureCancellation';

type PipefyNotice = { kind: 'success' | 'error'; message: string };
type ViewMode = 'grid' | 'list';

const PAGE_SIZE_LIST = 10;
const PAGE_SIZE_GRID = 12;
const CREATE_PROMOTION_ROUTE = '/briefing-promocoes/promocoes';

const XLSX_EXPORTERS: Record<string, XlsxExporter> = {
  t1: exportRebaixaSimples,
  t2: exportRebaixaProgressiva,
  t3: exportCompreEGanhe,
  t4a: exportListaSugeridaOriginal,
  t4b: exportListaSugeridaRecompra,
  t5: exportCupomSimples,
  t6: exportCupomProgressivo,
  t7: exportCashback,
  t8: exportRebaixaCupomGeral,
};

function hasXlsxExporter(record: HistoryRecord) {
  return record.templateId in XLSX_EXPORTERS;
}

function getNormalizedRecordValues(record: HistoryRecord, templateList: TemplateDef[]): FormValues {
  const template = templateList.find((item) => item.id === record.templateId) ?? null;
  return normalizeSellerFieldsInValues(record.values as FormValues, getSellerOptionsFromTemplate(template));
}

function exportRecordXlsx(record: HistoryRecord, templateList: TemplateDef[], briefingV4: boolean) {
  const exporter = XLSX_EXPORTERS[record.templateId];
  if (exporter) exporter(getNormalizedRecordValues(record, templateList), { briefingV4 });
}

function cloneFormValues(values: FormValues): FormValues {
  return Object.fromEntries(
    Object.entries(values).map(([key, value]) => [key, Array.isArray(value) ? [...value] : value]),
  ) as FormValues;
}

function buildReuseState(record: HistoryRecord, templateList: TemplateDef[]) {
  return {
    reuseBriefing: true,
    sourceBriefingId: record.id,
    templateId: record.templateId,
    values: cloneFormValues(getNormalizedRecordValues(record, templateList)),
  };
}

const TEMPLATE_ICONS: Record<string, ReactNode> = {
  t1: <Tag size={16} />,
  t2: <TrendingUp size={16} />,
  t3: <Gift size={16} />,
  t4a: <ListChecks size={16} />,
  t4b: <ListChecks size={16} />,
  t5: <BadgePercent size={16} />,
  t6: <BadgePercent size={16} />,
  t7: <CircleDollarSign size={16} />,
  t8: <Tag size={16} />,
};

const STATUS_FILTER_OPTIONS: { value: HistoryStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'ativo', label: 'Ativo' },
  { value: 'agendado', label: 'Agendado' },
  { value: 'rascunho', label: 'Rascunho' },
  { value: 'encerrado', label: 'Encerrado' },
];

const STATUS_BADGE_CLASSES: Record<HistoryStatus, string> = {
  ativo: 'bg-emerald-100 text-emerald-700 ring-emerald-200',
  agendado: 'bg-sky-100 text-sky-700 ring-sky-200',
  rascunho: 'bg-amber-100 text-amber-700 ring-amber-200',
  encerrado: 'bg-slate-100 text-slate-600 ring-slate-200',
};

const STATUS_CHIP_CLASSES: Record<HistoryStatus, string> = {
  ativo: 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
  agendado: 'border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100',
  rascunho: 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100',
  encerrado: 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100',
};

function formatDateRelative(iso: string) {
  if (!iso) return '—';

  const d = new Date(iso);
  const now = new Date();
  const diff = Math.floor((now.getTime() - d.getTime()) / 86400000);

  if (diff === 0) return 'Hoje';
  if (diff === 1) return 'Ontem';
  if (diff < 7) return `${diff} dias atrás`;

  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatCurrency(value: string) {
  if (!value) return '—';

  return `R$ ${Number(value).toLocaleString('pt-BR')}`;
}

function getPaginationItems(currentPage: number, pageCount: number): Array<number | 'ellipsis'> {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, index) => index + 1);
  }

  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, 'ellipsis', pageCount];
  }

  if (currentPage >= pageCount - 3) {
    return [1, 'ellipsis', pageCount - 4, pageCount - 3, pageCount - 2, pageCount - 1, pageCount];
  }

  return [1, 'ellipsis', currentPage - 1, currentPage, currentPage + 1, 'ellipsis', pageCount];
}

function StatusBadge({ status }: { status: HistoryStatus }) {
  return (
    <span
      className={`
        inline-flex h-5 items-center rounded px-2 text-[0.68rem] font-bold leading-none ring-1
        ${STATUS_BADGE_CLASSES[status]}
      `}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}
const PIPEFY_STATUS_LABELS: Record<PipefyStatus, string> = {
  not_sent: 'Nao enviado',
  queued: 'Na fila',
  processing: 'Enviando',
  sent: 'Enviado',
  failed: 'Falhou',
  sent_manual: 'Enviado manualmente',
  cancelled: 'Cancelado',
};

const PIPEFY_STATUS_CLASSES: Record<PipefyStatus, string> = {
  not_sent: 'bg-slate-100 text-slate-600 ring-slate-200',
  queued: 'bg-blue-50 text-blue-700 ring-blue-200',
  processing: 'bg-blue-100 text-blue-800 ring-blue-300',
  sent: 'bg-emerald-100 text-emerald-700 ring-emerald-200',
  failed: 'bg-red-100 text-red-700 ring-red-200',
  sent_manual: 'bg-cyan-100 text-cyan-800 ring-cyan-200',
  cancelled: 'bg-rose-100 text-rose-700 ring-rose-200',
};

const PIPEFY_ERROR_MESSAGES: Record<string, string> = {
  ...PIPEFY_FAILURE_CANCELLATION_MESSAGES,
  PIPEFY_BRIEFING_START_DATE_IN_PAST: PIPEFY_PAST_START_DATE_MESSAGE,
  PIPEFY_IMPORT_FAILED: 'O Pipefy informou que a importacao falhou; o briefing nao foi marcado como enviado.',
  PIPEFY_IMPORT_FAILED_WITH_CARDS: 'O Pipefy informou falha, mas criou cards. Confira os cards e seus vinculos no Pipefy antes de reenviar para evitar duplicacao.',
  PIPEFY_IMPORTER_UPLOAD_URL_ERROR: 'Nao foi possivel obter a URL de upload do Pipefy. Falhas temporarias sao tentadas novamente antes da importacao.',
  PIPEFY_IMPORTER_UPLOAD_URL_INVALID_RESPONSE: 'O Pipefy nao retornou URLs validas para enviar e ler o arquivo.',
  PIPEFY_IMPORTER_UPLOAD_ERROR: 'Nao foi possivel enviar o arquivo ao Pipefy. Nenhuma importacao foi solicitada nessa tentativa.',
  PIPEFY_IMPORT_ZERO_CARDS: 'O Pipefy processou o arquivo, mas nao criou nenhum card.',
  PIPEFY_IMPORT_PARTIAL: 'O Pipefy criou apenas parte dos cards esperados para o arquivo. Confira os cards no Pipefy antes de reenviar para evitar duplicacao.',
  PIPEFY_IMPORT_STATUS_TIMEOUT: 'A importacao excedeu o tempo limite de acompanhamento. A fila foi liberada no SI. Confira o resultado no Pipefy antes de reenviar.',
  PIPEFY_STALE_PROCESSING_STATE: 'O processamento terminou sem um resultado confirmado.',
  PIPEFY_IMPORT_BUSY: 'O Pipefy esta processando outra importacao. Esta promocao permanece na fila.',
  PIPEFY_IMPORT_REQUESTER_NOT_FOUND: 'O usuario logado nao possui nome e e-mail validos no sistema interno.',
  PIPEFY_REQUESTER_NOT_IN_ORGANIZATION: 'Seu e-mail nao esta cadastrado na organizacao do Pipefy.',
  PIPEFY_REQUESTER_NOT_IN_PIPE: 'Seu usuario do Pipefy nao possui acesso ao pipe de promocoes.',
  PIPEFY_CARD_ASSIGNEE_NOT_CONFIGURED: 'O campo de responsavel do Pipefy nao esta configurado.',
  PIPEFY_ORGANIZATION_NOT_FOUND: 'A organizacao configurada nao foi encontrada no Pipefy.',
  PIPEFY_RESPONSIBLE_FIELD_NOT_FOUND: 'O campo de responsavel nao foi encontrado no pipe do Pipefy.',
  PIPEFY_RESPONSIBLE_FIELD_INVALID_TYPE: 'O campo de responsavel configurado no Pipefy possui tipo invalido.',
  PIPEFY_CANCEL_ONLY_IMPORTED: 'Somente importacoes realizadas pelo sistema podem ser canceladas no Pipefy.',
  PIPEFY_CANCEL_JOB_NOT_FOUND: 'A importacao vinculada a esta promocao nao foi encontrada.',
  PIPEFY_CANCEL_PHASE_NOT_FOUND: 'A fase de cancelamento nao foi encontrada no Pipefy.',
  PIPEFY_CARD_IDS_NOT_FOUND: 'Nao foi possivel localizar os cards gerados por esta importacao.',
  PIPEFY_CARD_MAPPING_UNAVAILABLE: 'Nao foi possivel identificar com seguranca os cards desta importacao.',
  PIPEFY_CARD_MAPPING_AMBIGUOUS: 'Foram encontrados cards ambiguos; o cancelamento foi bloqueado por seguranca.',
  PIPEFY_CANCEL_PARTIAL: 'Alguns cards nao puderam ser cancelados. O status foi atualizado com os detalhes da falha.',
};

const PIPEFY_FILTER_OPTIONS: Array<{ value: PipefyStatus | 'all'; label: string }> = [
  { value: 'all', label: 'Todos' },
  { value: 'not_sent', label: 'Nao enviados' },
  { value: 'queued', label: 'Na fila' },
  { value: 'processing', label: 'Enviando' },
  { value: 'sent', label: 'Enviados' },
  { value: 'sent_manual', label: 'Enviados manualmente' },
  { value: 'cancelled', label: 'Cancelados' },
  { value: 'failed', label: 'Com falha' },
];

function getPipefyStatus(record: HistoryRecord): PipefyStatus {
  return record.pipefyStatus || 'not_sent';
}

function canCancelPipefy(record: HistoryRecord): boolean {
  return getPipefyStatus(record) === 'sent'
    && record.pipefyStatusSource !== 'manual'
    && Boolean(record.pipefyJobId)
    && Boolean(record.pipefyImportId);
}

type PipefyActionKind = 'send' | 'cancel' | 'pending' | 'locked';

interface PipefyAction {
  kind: PipefyActionKind;
  label: string;
  title: string;
  blockedByPastStartDate?: boolean;
}

function getPipefyAction(record: HistoryRecord, busy = false): PipefyAction {
  const status = getPipefyStatus(record);

  if (busy || status === 'queued' || status === 'processing') {
    return {
      kind: 'pending',
      label: 'Enviando ao Pipefy',
      title: 'Aguarde a conclusao do envio ao Pipefy',
    };
  }

  if (requiresManualPipefyCancellation(record.pipefyLastError)) {
    return {
      kind: 'locked',
      label: 'Revisar no Pipefy',
      title: PIPEFY_ERROR_MESSAGES[record.pipefyLastError!],
    };
  }

  if (hasPendingPipefyFailureCancellation(record.pipefyLastError)) {
    return {
      kind: 'pending',
      label: 'Cancelando no Pipefy',
      title: PIPEFY_ERROR_MESSAGES[record.pipefyLastError!],
    };
  }

  if (status === 'cancelled') {
    return {
      kind: 'locked',
      label: 'Cancelado no Pipefy',
      title: 'Esta promocao foi cancelada e nao pode ser enviada novamente',
    };
  }

  if (canCancelPipefy(record)) {
    return {
      kind: 'cancel',
      label: 'Cancelar no Pipefy',
      title: 'Cancelar esta promocao no Pipefy',
    };
  }

  if (status === 'sent' || status === 'sent_manual') {
    return {
      kind: 'locked',
      label: status === 'sent_manual' ? 'Enviado manualmente' : 'Enviado ao Pipefy',
      title: status === 'sent_manual'
        ? 'Esta promocao foi marcada como enviada manualmente'
        : 'Esta promocao ja foi enviada ao Pipefy',
    };
  }

  if (isPromotionStartDateInPast(record.values.inicioVigencia)) {
    return {
      kind: 'locked',
      label: 'Envio bloqueado',
      title: PIPEFY_PAST_START_DATE_MESSAGE,
      blockedByPastStartDate: true,
    };
  }

  return {
    kind: 'send',
    label: 'Enviar ao Pipefy',
    title: 'Enviar esta promocao ao Pipefy',
  };
}

function getPipefyStatusTitle(record: HistoryRecord): string {
  const status = getPipefyStatus(record);
  const details = [
    record.pipefyLastError
      ? PIPEFY_ERROR_MESSAGES[record.pipefyLastError] || record.pipefyLastError
      : 'Pipefy: ' + PIPEFY_STATUS_LABELS[status],
    record.pipefyImportId ? 'Importacao: ' + record.pipefyImportId : '',
  ].filter(Boolean);

  return details.join(' | ');
}

function PipefyFailureMessage({ record }: { record: HistoryRecord }) {
  if (getPipefyStatus(record) !== 'failed' || !record.pipefyLastError) return null;
  return (
    <p className="mt-2 text-xs leading-relaxed text-red-700" role="status">
      {PIPEFY_ERROR_MESSAGES[record.pipefyLastError] || 'O envio ao Pipefy falhou. Consulte os detalhes da tentativa no histórico de eventos.'}
    </p>
  );
}

function PipefyStatusBadge({ record }: { record: HistoryRecord }) {
  const status = getPipefyStatus(record);

  return (
    <span
      title={getPipefyStatusTitle(record)}
      className={`
        inline-flex h-5 items-center rounded px-2 text-[0.66rem] font-bold leading-none ring-1
        ${PIPEFY_STATUS_CLASSES[status]}
      `}
    >
      Pipefy: {PIPEFY_STATUS_LABELS[status]}
    </span>
  );
}


interface HistoryCardProps {
  record: HistoryRecord;
  isSelected: boolean;
  onSendPipefy: () => void;
  onCancelPipefy: () => void;
  pipefyBusy: boolean;
  canManagePipefy: boolean;
  selectionDisabled: boolean;
  onClick: () => void;
  onExportXlsx: () => void;
  onReuse: () => void;
  onToggleSelect: () => void;
}

function HistoryCard({
  record,
  isSelected,
  onSendPipefy,
  onCancelPipefy,
  pipefyBusy,
  canManagePipefy,
  selectionDisabled,
  onClick,
  onExportXlsx,
  onReuse,
  onToggleSelect,
}: HistoryCardProps) {
  const nomePromo = (record.values.nomePromo as string) || '(sem nome)';
  const nomeExibicao = (record.values.nomeExibicao as string) || '';
  const inicio = (record.values.inicioVigencia as string) || '';
  const fim = (record.values.fimVigencia as string) || '';
  const budget = (record.values.budgetValor as string) || '';

  const canExportXlsx = hasXlsxExporter(record);
  const pipefyAction = getPipefyAction(record, pipefyBusy);
  const pipefyActionDisabled = pipefyAction.kind === 'pending' || pipefyAction.kind === 'locked';
  const pipefyActionIsCancel = pipefyAction.kind === 'cancel';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className={`
        group h-full w-full cursor-pointer overflow-hidden rounded-2xl border bg-white text-left
        shadow-sm transition-all duration-150 hover:-translate-y-0.5 hover:shadow-md
        focus:outline-none focus:ring-2 focus:ring-blue-500/30
        ${isSelected ? 'border-blue-400 ring-2 ring-blue-500/20' : 'border-slate-200'}
      `}
      style={{ '--template-color': record.templateColor } as React.CSSProperties}
    >

      <div className="h-1 w-full bg-[var(--template-color)]" />

      <div className="p-4">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <label
              title={selectionDisabled ? 'Disponível apenas para o mesmo template selecionado' : 'Selecionar para exportar em lote'}
              aria-label="Selecionar briefing"
              onClick={(e) => e.stopPropagation()}
              className={`
                flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition-colors
                ${isSelected
                  ? 'border-blue-500 bg-blue-50'
                  : selectionDisabled
                    ? 'border-slate-200 bg-slate-50 opacity-50'
                    : 'border-slate-200 bg-white hover:bg-slate-50'
                }
              `}
            >
              <input
                type="checkbox"
                checked={isSelected}
                disabled={selectionDisabled}
                onChange={(e) => {
                  e.stopPropagation();
                  onToggleSelect();
                }}
                onKeyDown={(e) => e.stopPropagation()}
                className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
            </label>

            <div
              className="
                flex h-8 w-8 shrink-0 items-center justify-center rounded-lg
                bg-[color:var(--template-color)]/10 text-[var(--template-color)]
              "
              style={{
                backgroundColor: `${record.templateColor}18`,
                color: record.templateColor,
              }}
            >
              {TEMPLATE_ICONS[record.templateId]}
            </div>

            <div className="min-w-0">
              <span
                className="
                  block truncate text-[0.65rem] font-bold uppercase leading-none tracking-[0.3px]
                "
                style={{ color: record.templateColor }}
              >
                {record.templateName}
              </span>

              <h3 className="mt-1 truncate text-[0.85rem] font-bold leading-tight text-slate-900">
                {nomePromo}
              </h3>
            </div>
          </div>

          <div className="flex shrink-0 flex-col items-end gap-1">
            <StatusBadge status={record.status} />
            <PipefyStatusBadge record={record} />
          </div>
        </div>

        <PipefyFailureMessage record={record} />

        {nomeExibicao && (
          <p className="mb-3 line-clamp-2 text-xs leading-relaxed text-slate-500">
            {nomeExibicao}
          </p>
        )}

        <div className="my-3 h-px bg-slate-100" />

        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {inicio && (
            <div className="flex items-center gap-1.5 text-[0.7rem] text-slate-500">
              <CalendarDays size={12} className="text-slate-400" />
              <span>
                {inicio.slice(0, 10)} → {fim ? fim.slice(0, 10) : '—'}
              </span>
            </div>
          )}

          {budget && (
            <span className="text-[0.7rem] text-slate-500">
              Budget: {formatCurrency(budget)}
            </span>
          )}
        </div>

        <div className="mt-4 flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-1.5 text-[0.7rem] text-slate-500">
            <User size={12} className="shrink-0 text-slate-400" />
            <span className="truncate">{record.author}</span>
          </div>

          <span className="shrink-0 text-[0.68rem] text-slate-400">
            {formatDateRelative(record.updatedAt)}
          </span>
        </div>

        <div className="mt-3 flex items-center justify-end gap-1 border-t border-slate-100 pt-3">
          <button
            type="button"
            title="Reaproveitar"
            aria-label="Reaproveitar"
            onClick={(e) => {
              e.stopPropagation();
              onReuse();
            }}
            className="
              flex h-8 items-center justify-center gap-1 rounded-lg px-2 text-xs font-bold text-blue-700
              transition-colors hover:bg-blue-50
              focus:outline-none focus:ring-2 focus:ring-blue-500/30
            "
          >
            <RefreshCw size={16} />
            Reaproveitar
          </button>
          {canManagePipefy && canExportXlsx && (
            <Tooltip title={pipefyAction.title} arrow>
              <span className="inline-flex">
                <button
                  type="button"
                  aria-label={pipefyAction.label}
                  disabled={pipefyActionDisabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (pipefyActionIsCancel) onCancelPipefy();
                    else onSendPipefy();
                  }}
                  className={`
                    flex h-8 w-8 items-center justify-center rounded-lg transition-colors
                    disabled:cursor-not-allowed disabled:opacity-45
                    focus:outline-none focus:ring-2 focus:ring-blue-500/30
                    ${pipefyActionIsCancel ? 'text-rose-700 hover:bg-rose-50' : 'text-blue-700 hover:bg-blue-50'}
                  `}
                >
                  {pipefyAction.kind === 'pending'
                    ? <LoaderCircle size={16} className="animate-spin" />
                    : pipefyAction.kind === 'cancel' || getPipefyStatus(record) === 'cancelled'
                      ? <Ban size={16} />
                      : pipefyAction.blockedByPastStartDate
                        ? <AlertCircle size={16} />
                        : pipefyAction.kind === 'locked'
                          ? <CheckCircle2 size={16} />
                          : <CloudUpload size={16} />}
                </button>
              </span>
            </Tooltip>
          )}


          {canExportXlsx && (
            <button
              type="button"
              title="Baixar XLSX"
              aria-label="Baixar XLSX"
              onClick={(e) => {
                e.stopPropagation();
                onExportXlsx();
              }}
              className="
                flex h-8 items-center justify-center gap-1 rounded-lg px-2 text-xs font-bold text-emerald-700
                transition-colors hover:bg-emerald-50
                focus:outline-none focus:ring-2 focus:ring-emerald-500/30
              "
            >
              <FileSpreadsheet size={16} />

            </button>
          )}

          <button
            type="button"
            title="Visualizar"
            aria-label="Visualizar"
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
            className="
              flex h-8 items-center justify-center gap-1 rounded-lg px-2 text-xs font-semibold text-slate-500
              transition-colors hover:bg-slate-100 hover:text-slate-800
              focus:outline-none focus:ring-2 focus:ring-blue-500/30
            "
          >
            <Eye size={16} />
            Visualizar
          </button>
        </div>
      </div>
    </div>
  );
}

interface HistoryListRowProps {
  record: HistoryRecord;
  isSelected: boolean;
  selectionDisabled: boolean;
  onClick: () => void;
  onSendPipefy: () => void;
  onCancelPipefy: () => void;
  pipefyBusy: boolean;
  canManagePipefy: boolean;
  onExportXlsx: () => void;
  onReuse: () => void;
  onToggleSelect: () => void;
}

function HistoryListHeader() {
  const labelClass = 'text-[0.68rem] font-bold uppercase tracking-[0.4px] text-slate-500';

  return (
    <div
      className="
        hidden items-stretch rounded-xl border border-slate-200 bg-slate-50 shadow-sm sm:flex
      "
    >
      <div className={`flex w-[52px] shrink-0 items-center pl-3 ${labelClass}`}>
        Sel.
      </div>

      <div className="w-1 shrink-0" />

      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-4 px-4 py-2.5">
        <div className={`min-w-[200px] flex-[2] ${labelClass}`}>
          Promoção
        </div>

        <div className={`hidden min-w-[140px] sm:block ${labelClass}`}>
          Template
        </div>

        <div className={`hidden min-w-[160px] md:block ${labelClass}`}>
          Vigência
        </div>

        <div className={`hidden min-w-[100px] lg:block ${labelClass}`}>
          Budget
        </div>

        <div className={`ml-auto shrink-0 text-right ${labelClass}`}>
          Status / Responsável
        </div>
      </div>

      <div className={`flex w-[260px] shrink-0 items-center justify-end pr-3 ${labelClass}`}>
        Ações
      </div>
    </div>
  );
}

function HistoryListRow({
  record,
  isSelected,
  onSendPipefy,
  onCancelPipefy,
  pipefyBusy,
  canManagePipefy,
  selectionDisabled,
  onClick,
  onExportXlsx,
  onReuse,
  onToggleSelect,
}: HistoryListRowProps) {
  const nomePromo = (record.values.nomePromo as string) || '(sem nome)';
  const nomeExibicao = (record.values.nomeExibicao as string) || '';
  const inicio = (record.values.inicioVigencia as string) || '';
  const fim = (record.values.fimVigencia as string) || '';
  const budget = (record.values.budgetValor as string) || '';
  const canExportXlsx = hasXlsxExporter(record);
  const pipefyAction = getPipefyAction(record, pipefyBusy);
  const pipefyActionDisabled = pipefyAction.kind === 'pending' || pipefyAction.kind === 'locked';
  const pipefyActionIsCancel = pipefyAction.kind === 'cancel';

  return (
    <div
      className={`
        group overflow-hidden rounded-xl border bg-white shadow-sm
        transition-all duration-150 hover:shadow-md
        ${isSelected ? 'border-blue-400 ring-2 ring-blue-500/20' : 'border-slate-200'}
      `}
      style={{ '--template-color': record.templateColor } as React.CSSProperties}
    >
      <div className="flex items-stretch">
        <div className="flex items-center pl-3">
          <label
            title={selectionDisabled ? 'Disponível apenas para o mesmo template selecionado' : 'Selecionar para exportar em lote'}
            aria-label="Selecionar briefing"
            onClick={(e) => e.stopPropagation()}
            className={`
              flex h-8 w-8 items-center justify-center rounded-lg border transition-colors
              ${isSelected
                ? 'border-blue-500 bg-blue-50'
                : selectionDisabled
                  ? 'border-slate-200 bg-slate-50 opacity-50'
                  : 'border-slate-200 bg-white hover:bg-slate-50'
              }
            `}
          >
            <input
              type="checkbox"
              checked={isSelected}
              disabled={selectionDisabled}
              onChange={(e) => {
                e.stopPropagation();
                onToggleSelect();
              }}
              onKeyDown={(e) => e.stopPropagation()}
              className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
          </label>
        </div>

        <button
          type="button"
          onClick={onClick}
          className="flex min-w-0 flex-1 items-center text-left focus:outline-none"
        >
          <div className="w-1 self-stretch bg-[var(--template-color)]" />

          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-4 px-4 py-3">
            <div className="flex min-w-[200px] flex-[2] items-center gap-3">
              <div
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
                style={{
                  backgroundColor: `${record.templateColor}18`,
                  color: record.templateColor,
                }}
              >
                {TEMPLATE_ICONS[record.templateId]}
              </div>

              <div className="min-w-0">
                <h3 className="truncate text-sm font-bold text-slate-900">
                  {nomePromo}
                </h3>

                <PipefyFailureMessage record={record} />

                {nomeExibicao && (
                  <p className="mt-0.5 truncate text-xs text-slate-500">
                    {nomeExibicao}
                  </p>
                )}
              </div>
            </div>

            <div className="hidden min-w-[140px] items-center gap-2 sm:flex">
              <span
                className="rounded-md border px-2 py-1 text-[0.65rem] font-bold"
                style={{
                  backgroundColor: `${record.templateColor}15`,
                  borderColor: `${record.templateColor}30`,
                  color: record.templateColor,
                }}
              >
                {record.templateNumber}
              </span>

              <span className="truncate text-xs text-slate-500">
                {record.templateName}
              </span>
            </div>

            <div className="hidden min-w-[160px] md:block">
              {inicio ? (
                <div className="flex items-center gap-1.5 text-xs text-slate-500">
                  <CalendarDays size={12} className="text-slate-400" />
                  <span>
                    {inicio.slice(0, 10)} → {fim ? fim.slice(0, 10) : '—'}
                  </span>
                </div>
              ) : (
                <span className="text-xs text-slate-400">Sem vigência</span>
              )}
            </div>

            <div className="hidden min-w-[100px] lg:block">
              <span
                className={`
                  text-xs
                  ${budget ? 'font-semibold text-slate-800' : 'font-normal text-slate-400'}
                `}
              >
                {budget ? formatCurrency(budget) : '—'}
              </span>
            </div>

            <div className="ml-auto flex shrink-0 flex-col items-end gap-1">
              <StatusBadge status={record.status} />
              <PipefyStatusBadge record={record} />

              <div className="flex items-center gap-1 text-[0.68rem] text-slate-400">
                <User size={11} />
                <span>{record.author}</span>
              </div>

              <span className="text-[0.65rem] text-slate-400">
                {formatDateRelative(record.updatedAt)}
              </span>
            </div>
          </div>
        </button>

        <div className="flex shrink-0 items-center gap-1 pr-3">
          <button
            type="button"
            title="Reaproveitar"
            aria-label="Reaproveitar"
            onClick={(e) => {
              e.stopPropagation();
              onReuse();
            }}
            className="
              flex h-8 items-center justify-center gap-1 rounded-lg px-2 text-xs font-bold text-blue-700
              transition-colors hover:bg-blue-50
              focus:outline-none focus:ring-2 focus:ring-blue-500/30
            "
          >
            <RefreshCw size={16} />
            <span className="hidden sm:inline">Reaproveitar</span>
          </button>
          {canManagePipefy && canExportXlsx && (
            <Tooltip title={pipefyAction.title} arrow>
              <span className="inline-flex">
                <button
                  type="button"
                  aria-label={pipefyAction.label}
                  disabled={pipefyActionDisabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (pipefyActionIsCancel) onCancelPipefy();
                    else onSendPipefy();
                  }}
                  className={`
                    flex h-8 items-center justify-center gap-1 rounded-lg px-2 text-xs font-bold
                    transition-colors disabled:cursor-not-allowed disabled:opacity-45
                    focus:outline-none focus:ring-2 focus:ring-blue-500/30
                    ${pipefyActionIsCancel ? 'text-rose-700 hover:bg-rose-50' : 'text-blue-700 hover:bg-blue-50'}
                  `}
                >
                  {pipefyAction.kind === 'pending'
                    ? <LoaderCircle size={16} className="animate-spin" />
                    : pipefyAction.kind === 'cancel' || getPipefyStatus(record) === 'cancelled'
                      ? <Ban size={16} />
                      : pipefyAction.blockedByPastStartDate
                        ? <AlertCircle size={16} />
                        : pipefyAction.kind === 'locked'
                          ? <CheckCircle2 size={16} />
                          : <CloudUpload size={16} />}
                  <span className="hidden xl:inline">{pipefyAction.label}</span>
                </button>
              </span>
            </Tooltip>
          )}


          {canExportXlsx && (
            <button
              type="button"
              title="Baixar XLSX"
              aria-label="Baixar XLSX"
              onClick={(e) => {
                e.stopPropagation();
                onExportXlsx();
              }}
              className="
                flex h-8 items-center justify-center gap-1 rounded-lg px-2 text-xs font-bold text-emerald-700
                transition-colors hover:bg-emerald-50
                focus:outline-none focus:ring-2 focus:ring-emerald-500/30
              "
            >
              <FileSpreadsheet size={16} />
              Baixar
            </button>
          )}

          <button
            type="button"
            title="Visualizar"
            aria-label="Visualizar"
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
            className="
              flex h-8 w-8 items-center justify-center rounded-lg text-slate-500
              transition-colors hover:bg-slate-100 hover:text-slate-800
              focus:outline-none focus:ring-2 focus:ring-blue-500/30
            "
          >
            <Eye size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

interface PaginationProps {
  page: number;
  pageCount: number;
  onChange: (page: number) => void;
}

function Pagination({ page, pageCount, onChange }: PaginationProps) {
  const items = getPaginationItems(page, pageCount);

  const goToPage = (nextPage: number) => {
    if (nextPage < 1 || nextPage > pageCount || nextPage === page) return;
    onChange(nextPage);
  };

  const buttonBaseClass =
    'flex h-9 min-w-9 items-center justify-center rounded-lg border px-2 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/30';

  return (
    <div className="flex flex-wrap items-center justify-center gap-1.5">
      <button
        type="button"
        onClick={() => goToPage(1)}
        disabled={page === 1}
        className={`${buttonBaseClass} border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40`}
        aria-label="Primeira página"
      >
        <ChevronsLeft size={16} />
      </button>

      <button
        type="button"
        onClick={() => goToPage(page - 1)}
        disabled={page === 1}
        className={`${buttonBaseClass} border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40`}
        aria-label="Página anterior"
      >
        <ChevronLeft size={16} />
      </button>

      {items.map((item, index) => {
        if (item === 'ellipsis') {
          return (
            <span
              key={`ellipsis-${index}`}
              className="flex h-9 min-w-9 items-center justify-center text-sm font-semibold text-slate-400"
            >
              ...
            </span>
          );
        }

        const isActive = item === page;

        return (
          <button
            key={item}
            type="button"
            onClick={() => goToPage(item)}
            className={
              isActive
                ? `${buttonBaseClass} border-blue-600 bg-blue-600 text-white`
                : `${buttonBaseClass} border-slate-200 bg-white text-slate-600 hover:bg-slate-50`
            }
          >
            {item}
          </button>
        );
      })}

      <button
        type="button"
        onClick={() => goToPage(page + 1)}
        disabled={page === pageCount}
        className={`${buttonBaseClass} border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40`}
        aria-label="Próxima página"
      >
        <ChevronRight size={16} />
      </button>

      <button
        type="button"
        onClick={() => goToPage(pageCount)}
        disabled={page === pageCount}
        className={`${buttonBaseClass} border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40`}
        aria-label="Última página"
      >
        <ChevronsRight size={16} />
      </button>
    </div>
  );
}

export default function HistoryPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const templates = useTemplates();
  const permissions = GetPermissions();
  const briefingV4 = isBriefingV4Enabled(permissions);
  const canManagePipefy = Boolean(
    permissions?.some((feature) => feature?.name === 'briefing-promocoes-pipefy-import'),
  );
  const canCancelPipefyCards = Boolean(
    permissions?.some((feature) => feature?.name === 'briefing-promocoes-pipefy-cancel-cards'),
  );
  const [records, setRecords] = useState<HistoryRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<HistoryStatus | 'all'>('all');
  const [pipefyFilter, setPipefyFilter] = useState<PipefyStatus | 'all'>('all');
  const [templateFilter, setTemplateFilter] = useState('all');
  const [authorFilter, setAuthorFilter] = useState('all');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [page, setPage] = useState(1);
  const [selectedRecord, setSelectedRecord] = useState<HistoryRecord | null>(null);
  const [selectedRecordIds, setSelectedRecordIds] = useState<string[]>([]);
  const [pipefyBusyIds, setPipefyBusyIds] = useState<Set<string>>(() => new Set());
  const [pipefySendTargets, setPipefySendTargets] = useState<HistoryRecord[]>([]);
  const [pipefyCancelTargets, setPipefyCancelTargets] = useState<HistoryRecord[]>([]);
  const previousPipefyRecords = useRef<Map<string, HistoryRecord>>(new Map());
  const [pipefyNotice, setPipefyNotice] = useState<PipefyNotice | null>(null);
  const [cancelCardsModalOpen, setCancelCardsModalOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [autoOpenHandledId, setAutoOpenHandledId] = useState<string | null>(null);


  const selectedBriefingIdFromNavigation = useMemo(() => {
    const state = location.state as { selectedBriefingId?: string | number; openBriefing?: boolean } | null;
    const params = new URLSearchParams(location.search);
    const idFromState = state?.selectedBriefingId;
    const idFromQuery = params.get('briefingId');

    if (idFromState !== undefined && idFromState !== null && String(idFromState).trim() !== '') {
      return String(idFromState);
    }

    if (idFromQuery !== null && String(idFromQuery).trim() !== '') {
      return String(idFromQuery);
    }

    return null;
  }, [location.search, location.state]);

  const loadRecords = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setLoadError(null);
      const data = await fetchAllBriefingPromocoes();
      setRecords(data);
    } catch (error) {
      console.log(error);
      setLoadError(error instanceof Error ? error.message : 'Não foi possível carregar o histórico de promoções.');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    loadRecords();
  }, []);
  useEffect(() => {
    const state = location.state as { pipefyQueued?: boolean; pipefyError?: string } | null;

    if (state?.pipefyError) {
      setPipefyNotice({
        kind: 'error',
        message: `Briefing salvo, mas o envio ao Pipefy falhou: ${state.pipefyError}`,
      });
    } else if (state?.pipefyQueued) {
      setPipefyNotice({
        kind: 'success',
        message: 'Briefing salvo e adicionado a fila de envio do Pipefy.',
      });
    }
  }, [location.state]);

  useEffect(() => {
    const changedFailure = records.find((record) => {
      const previous = previousPipefyRecords.current.get(String(record.id));
      return previous && getPipefyStatus(record) === 'failed'
        && (previous.pipefyLastError !== record.pipefyLastError
          || getPipefyStatus(previous) !== 'failed')
        && (['queued', 'processing'].includes(getPipefyStatus(previous))
          || hasPendingPipefyFailureCancellation(previous.pipefyLastError));
    });
    previousPipefyRecords.current = new Map(records.map((record) => [String(record.id), record]));
    if (changedFailure) {
      setPipefyNotice({ kind: 'error', message: getPipefyStatusTitle(changedFailure) });
    }
  }, [records]);

  useEffect(() => {
    const hasPendingPipefy = records.some((record) => {
      const status = getPipefyStatus(record);
      return status === 'queued' || status === 'processing'
        || hasPendingPipefyFailureCancellation(record.pipefyLastError);
    });
    if (!hasPendingPipefy) return undefined;

    const timer = window.setInterval(() => {
      void loadRecords(true);
    }, 10000);

    return () => window.clearInterval(timer);
  }, [records]);



  useEffect(() => {
    if (!selectedBriefingIdFromNavigation) return;
    if (loading) return;
    if (autoOpenHandledId === selectedBriefingIdFromNavigation) return;

    const openCreatedBriefing = async () => {
      try {
        setAutoOpenHandledId(selectedBriefingIdFromNavigation);
        setSearch('');
        setStatusFilter('all');
        setTemplateFilter('all');
        setAuthorFilter('all');
        setPipefyFilter('all');
        setPage(1);

        const localRecord = records.find((record) => String(record.id) === selectedBriefingIdFromNavigation);
        const recordToOpen = localRecord || await fetchBriefingPromocao(selectedBriefingIdFromNavigation);

        if (recordToOpen) {
          setSelectedRecord(recordToOpen);
          setDrawerOpen(true);
        }
      } catch (error) {
        console.log(error);
      }
    };

    openCreatedBriefing();
  }, [autoOpenHandledId, loading, records, selectedBriefingIdFromNavigation]);

  const activeFilterCount = [
    statusFilter !== 'all' ? 1 : 0,
    templateFilter !== 'all' ? 1 : 0,
    authorFilter !== 'all' ? 1 : 0,
    pipefyFilter !== 'all' ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  const allAuthors = useMemo(
    () => Array.from(new Set(records.map((r) => r.author).filter(Boolean))).sort(),
    [records],
  );

  const allTemplates = useMemo(
    () => templates.map((template) => ({ id: template.id, name: template.name })),
    [templates],
  );

  const recordsById = useMemo(
    () => new Map(records.map((record) => [String(record.id), record])),
    [records],
  );

  const selectedRecords = useMemo(
    () => selectedRecordIds
      .map((id) => recordsById.get(id))
      .filter((record): record is HistoryRecord => Boolean(record)),
    [recordsById, selectedRecordIds],
  );

  const selectedRecordIdSet = useMemo(
    () => new Set(selectedRecordIds),
    [selectedRecordIds],
  );

  const selectedTemplateId = selectedRecords[0]?.templateId ?? null;
  const selectedTemplateName = selectedRecords[0]?.templateName ?? '';
  const selectedPipefyBusy = selectedRecords.some((record) => {
    const status = getPipefyStatus(record);
    return pipefyBusyIds.has(String(record.id)) || status === 'queued' || status === 'processing'
      || hasPendingPipefyFailureCancellation(record.pipefyLastError);
  });
  const selectedCancellationPending = selectedRecords.some((record) =>
    hasPendingPipefyFailureCancellation(record.pipefyLastError),
  );
  const selectedCancellationManual = selectedRecords.some((record) =>
    requiresManualPipefyCancellation(record.pipefyLastError),
  );
  const selectedPipefyActionKind: PipefyActionKind = selectedPipefyBusy
    ? 'pending'
    : selectedRecords.length > 0 && selectedRecords.every(canCancelPipefy)
      ? 'cancel'
      : selectedRecords.length > 0
        && selectedRecords.every((record) => getPipefyAction(record).kind === 'send')
        ? 'send'
        : 'locked';
  const selectedPastStartDateBlocked = selectedRecords.some(
    (record) => getPipefyAction(record).blockedByPastStartDate,
  );
  const selectedPipefyActionLabel = selectedPipefyActionKind === 'cancel'
    ? 'Cancelar no Pipefy'
    : selectedPipefyActionKind === 'pending'
      ? selectedCancellationPending ? 'Cancelando no Pipefy' : 'Enviando ao Pipefy'
      : selectedPipefyActionKind === 'send'
        ? 'Enviar ao Pipefy'
        : selectedCancellationManual
          ? 'Revisar no Pipefy'
          : selectedPastStartDateBlocked
            ? 'Envio bloqueado'
            : selectedRecords.every((record) => getPipefyStatus(record) === 'cancelled')
              ? 'Cancelado no Pipefy'
              : 'Acao indisponivel';
  const selectedPipefyActionTitle = selectedPipefyActionKind === 'pending'
    ? selectedCancellationPending
      ? PIPEFY_FAILURE_CANCELLATION_MESSAGES.PIPEFY_IMPORT_FAILED_CANCELLATION_PENDING
      : 'Aguarde a conclusão do envio ao Pipefy.'
    : selectedPipefyActionKind === 'cancel'
      ? 'Cancelar as promoções selecionadas no Pipefy.'
      : selectedPipefyActionKind === 'send'
        ? 'Enviar as promoções selecionadas ao Pipefy.'
        : selectedCancellationManual
          ? PIPEFY_FAILURE_CANCELLATION_MESSAGES.PIPEFY_IMPORT_FAILED_CANCELLATION_MANUAL_REQUIRED
          : selectedPastStartDateBlocked
            ? PIPEFY_PAST_START_DATE_MESSAGE
            : 'A ação no Pipefy não está disponível para esta seleção.';
  const selectedPipefyActionDisabled = selectedPipefyActionKind === 'pending'
    || selectedPipefyActionKind === 'locked';
  const pipefySendBusy = pipefySendTargets.some((record) =>
    pipefyBusyIds.has(String(record.id)),
  );
  const pipefyCancelBusy = pipefyCancelTargets.some((record) =>
    pipefyBusyIds.has(String(record.id)),
  );

  useEffect(() => {
    setSelectedRecordIds((prev) => {
      const next = prev.filter((id) => recordsById.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [recordsById]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();

    return records.filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (templateFilter !== 'all' && r.templateId !== templateFilter) return false;
      if (authorFilter !== 'all' && r.author !== authorFilter) return false;
      if (pipefyFilter !== 'all' && getPipefyStatus(r) !== pipefyFilter) return false;

      if (q) {
        const nomePromo = ((r.values.nomePromo as string) ?? '').toLowerCase();
        const nomeExibicao = ((r.values.nomeExibicao as string) ?? '').toLowerCase();
        const descricao = ((r.values.descricao as string) ?? '').toLowerCase();
        const codigoCupom = ((r.values.codigoCupom as string) ?? '').toLowerCase();
        const author = r.author.toLowerCase();

        if (
          !nomePromo.includes(q) &&
          !nomeExibicao.includes(q) &&
          !descricao.includes(q) &&
          !codigoCupom.includes(q) &&
          !r.templateName.toLowerCase().includes(q) &&
          !author.includes(q) &&
          !r.id.toLowerCase().includes(q)
        ) {
          return false;
        }
      }

      return true;
    });
  }, [records, search, statusFilter, templateFilter, authorFilter, pipefyFilter]);

  const pageSize = viewMode === 'list' ? PAGE_SIZE_LIST : PAGE_SIZE_GRID;
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const paginated = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  const stats = useMemo(
    () => ({
      total: records.length,
      ativos: records.filter((r) => r.status === 'ativo').length,
      agendados: records.filter((r) => r.status === 'agendado').length,
      rascunhos: records.filter((r) => r.status === 'rascunho').length,
    }),
    [records],
  );

  const handleFilterChange = (fn: () => void) => {
    fn();
    setPage(1);
  };

  const handleOpenRecord = (record: HistoryRecord) => {
    setSelectedRecord(record);
    setDrawerOpen(true);
  };

  const handleExportRecordXlsx = (record: HistoryRecord) => {
    setPipefyNotice(null);
    try {
      exportRecordXlsx(record, templates, briefingV4);
    } catch (error) {
      setPipefyNotice({
        kind: 'error',
        message: error instanceof Error ? error.message : 'Não foi possível exportar o briefing. Revise os dados e tente novamente.',
      });
    }
  };

  const handleToggleRecordSelection = (record: HistoryRecord) => {
    if (!hasXlsxExporter(record)) return;

    const recordId = String(record.id);

    setSelectedRecordIds((prev) => {
      if (prev.includes(recordId)) {
        return prev.filter((id) => id !== recordId);
      }

      const firstSelectedRecord = prev
        .map((id) => recordsById.get(id))
        .find((item): item is HistoryRecord => Boolean(item));

      if (firstSelectedRecord && firstSelectedRecord.templateId !== record.templateId) {
        return prev;
      }

      return [...prev, recordId];
    });
  };

  const handleClearSelection = () => {
    setSelectedRecordIds([]);
  };

  const handleExportSelectedXlsx = () => {
    if (!selectedTemplateId || selectedRecords.length === 0) return;

    const sameTemplateRecords = selectedRecords.filter(
      (record) => record.templateId === selectedTemplateId,
    );

    setPipefyNotice(null);
    try {
      exportBriefingsByTemplate(
        selectedTemplateId,
        sameTemplateRecords.map((record) => getNormalizedRecordValues(record, templates)),
        { briefingV4 },
      );
    } catch (error) {
      setPipefyNotice({
        kind: 'error',
        message: error instanceof Error ? error.message : 'Não foi possível exportar os briefings. Revise os dados e tente novamente.',
      });
    }
  };
  const setPipefyBusy = (recordIds: string[], busy: boolean) => {
    setPipefyBusyIds((current) => {
      const next = new Set(current);
      recordIds.forEach((id) => {
        if (busy) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  };

  const getPipefyErrorMessage = (error: unknown): string => {
    const apiCode = (error as { response?: { data?: { error?: string } } })
      ?.response?.data?.error;

    if (apiCode === 'PIPEFY_BRIEFING_ALREADY_PENDING') {
      return 'Uma das promocoes selecionadas ja esta na fila do Pipefy.';
    }
    if (apiCode === 'PIPEFY_CANCELLED_BRIEFING_LOCKED') {
      return 'Promocoes canceladas nao podem ser enviadas ou alteradas novamente no Pipefy.';
    }
    if (apiCode === 'PIPEFY_BRIEFING_NOT_CONFIGURED') {
      return 'A integracao com o Pipefy ainda nao esta configurada.';
    }
    if (apiCode === 'PIPEFY_UNMAPPED_COLUMNS') {
      return 'O XLSX possui colunas que nao foram reconhecidas no Pipefy.';
    }
    if (apiCode && PIPEFY_ERROR_MESSAGES[apiCode]) {
      return PIPEFY_ERROR_MESSAGES[apiCode];
    }

    return error instanceof Error ? error.message : 'Nao foi possivel concluir a operacao no Pipefy.';
  };

  const handleSendRecordsToPipefy = async (recordsToSend: HistoryRecord[]) => {
    if (!canManagePipefy || recordsToSend.length === 0) return;

    const templateId = recordsToSend[0].templateId;
    if (recordsToSend.some((record) => record.templateId !== templateId)) {
      setPipefyNotice({
        kind: 'error',
        message: 'Selecione apenas promocoes do mesmo template para enviar ao Pipefy.',
      });
      return;
    }

    const unavailableRecord = recordsToSend.find(
      (record) => getPipefyAction(record).kind !== 'send',
    );
    if (unavailableRecord) {
      const status = getPipefyStatus(unavailableRecord);
      const unavailableAction = getPipefyAction(unavailableRecord);
      setPipefySendTargets([]);
      setPipefyNotice({
        kind: 'error',
        message: unavailableAction.blockedByPastStartDate
          ? unavailableAction.title
          : status === 'cancelled'
            ? 'Promocoes canceladas nao podem ser enviadas novamente.'
          : hasPendingPipefyFailureCancellation(unavailableRecord.pipefyLastError)
            || requiresManualPipefyCancellation(unavailableRecord.pipefyLastError)
            ? unavailableAction.title
          : status === 'queued' || status === 'processing'
            ? 'Aguarde o envio em andamento antes de reenviar essa promocao.'
            : 'Uma das promocoes selecionadas ja foi enviada ao Pipefy.',
      });
      return;
    }

    const recordIds = recordsToSend.map((record) => String(record.id));
    setPipefyBusy(recordIds, true);
    setPipefyNotice(null);

    try {
      const file = createBriefingsXlsxFile(
        templateId,
        recordsToSend.map((record) => getNormalizedRecordValues(record, templates)),
        { briefingV4 },
      );
      if (!file) throw new Error('Nao foi possivel gerar o XLSX para o Pipefy.');

      const result = await enqueueBriefingsPipefyImport({
        templateId,
        briefingIds: recordIds,
        file,
      });
      const now = new Date().toISOString();
      const applyQueuedStatus = (record: HistoryRecord): HistoryRecord => (
        recordIds.includes(String(record.id))
          ? {
              ...record,
              pipefyStatus: 'queued',
              pipefyStatusSource: 'system',
              pipefyJobId: result.jobId,
              pipefyLastError: undefined,
              pipefyStatusUpdatedAt: now,
            }
          : record
      );

      setRecords((current) => current.map(applyQueuedStatus));
      setSelectedRecord((current) => current ? applyQueuedStatus(current) : current);
      setSelectedRecordIds([]);
      setPipefySendTargets([]);
      setPipefyNotice({
        kind: 'success',
        message: `${recordIds.length} ${recordIds.length === 1 ? 'promocao adicionada' : 'promocoes adicionadas'} a fila do Pipefy.`,
      });
    } catch (error) {
      setPipefyNotice({ kind: 'error', message: getPipefyErrorMessage(error) });
    } finally {
      setPipefyBusy(recordIds, false);
    }
  };

  const handleCancelPipefy = async () => {
    if (!canManagePipefy || pipefyCancelTargets.length === 0) return;
    if (pipefyCancelTargets.some((record) => !canCancelPipefy(record))) {
      setPipefyCancelTargets([]);
      setPipefyNotice({
        kind: 'error',
        message: 'O status da promocao mudou e ela nao esta mais disponivel para cancelamento.',
      });
      void loadRecords(true);
      return;
    }

    const recordIds = pipefyCancelTargets.map((record) => String(record.id));
    setPipefyBusy(recordIds, true);
    setPipefyNotice(null);

    try {
      const result = await cancelBriefingsPipefy(recordIds);
      const affectedIds = new Set(result.briefingIds.map(String));
      const now = new Date().toISOString();
      const applyCancelledStatus = (record: HistoryRecord): HistoryRecord => (
        affectedIds.has(String(record.id))
          ? {
              ...record,
              pipefyStatus: 'cancelled',
              pipefyStatusSource: 'system',
              pipefyLastError: undefined,
              pipefyStatusUpdatedAt: now,
            }
          : record
      );

      setRecords((current) => current.map(applyCancelledStatus));
      setSelectedRecord((current) => current ? applyCancelledStatus(current) : current);
      setPipefyCancelTargets([]);
      setSelectedRecordIds([]);
      setPipefyNotice({
        kind: 'success',
        message: `${result.cardCount} ${result.cardCount === 1 ? 'card cancelado' : 'cards cancelados'} no Pipefy.`,
      });
    } catch (error) {
      setPipefyNotice({ kind: 'error', message: getPipefyErrorMessage(error) });
      void loadRecords(true);
    } finally {
      setPipefyBusy(recordIds, false);
    }
  };

  const handleReuseRecord = (record: HistoryRecord) => {
    navigate(CREATE_PROMOTION_ROUTE, { state: buildReuseState(record, templates) });
  };

  const handleRecordDeleted = (record: HistoryRecord) => {
    setRecords((prev) => prev.filter((item) => item.id !== record.id));
    setSelectedRecordIds((prev) => prev.filter((id) => id !== String(record.id)));
    setSelectedRecord(null);
    setDrawerOpen(false);
  };

  const handleClearFilters = () => {
    setStatusFilter('all');
    setTemplateFilter('all');
    setAuthorFilter('all');
    setPipefyFilter('all');
    setSearch('');
    setPage(1);
  };

  const isRecordSelectionDisabled = (record: HistoryRecord) => {
    const isSelected = selectedRecordIdSet.has(String(record.id));
    return !isSelected && (
      !hasXlsxExporter(record) ||
      Boolean(selectedTemplateId && record.templateId !== selectedTemplateId)
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <main className="mx-auto w-full">
        {pipefyNotice && (
          <section
            className={`
              mb-4 flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-sm
              ${pipefyNotice.kind === 'success'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                : 'border-red-200 bg-red-50 text-red-700'}
            `}
          >
            <span className="flex items-center gap-2">
              {pipefyNotice.kind === 'success'
                ? <CheckCircle2 size={18} className="shrink-0" />
                : <AlertCircle size={18} className="shrink-0" />}
              {pipefyNotice.message}
            </span>
            <button
              type="button"
              title="Fechar aviso"
              aria-label="Fechar aviso"
              onClick={() => setPipefyNotice(null)}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg hover:bg-black/5"
            >
              <X size={15} />
            </button>
          </section>
        )}

        {loadError && (
          <section className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {loadError}
            <button
              type="button"
              onClick={() => void loadRecords()}
              className="ml-3 font-bold underline"
            >
              Tentar novamente
            </button>
          </section>
        )}

        {loading && (
          <section className="mb-4 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-500 shadow-sm">
            Carregando histórico de promoções...
          </section>
        )}

        <section className="mb-6 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm">
            {stats.total} total
          </span>

          <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1.5 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200">
            {stats.ativos} ativos
          </span>

          <span className="inline-flex items-center rounded-full bg-sky-100 px-3 py-1.5 text-xs font-semibold text-sky-700 ring-1 ring-sky-200">
            {stats.agendados} agendados
          </span>

          <span className="inline-flex items-center rounded-full bg-amber-100 px-3 py-1.5 text-xs font-semibold text-amber-700 ring-1 ring-amber-200">
            {stats.rascunhos} rascunhos
          </span>
          {canCancelPipefyCards && (
            <button
              type="button"
              onClick={() => setCancelCardsModalOpen(true)}
              className="
                ml-auto inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-rose-600
                px-3 text-xs font-bold text-white shadow-sm transition-colors hover:bg-rose-700
                focus:outline-none focus:ring-2 focus:ring-rose-500/30
              "
            >
              <Ban size={15} />
              Cancelar cards por ID
            </button>
          )}
        </section>

        <section
          className="
            mb-6 flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm
            xl:flex-row xl:items-center
          "
        >
          <div className="relative min-w-[220px] flex-1">
            <Search
              size={18}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />

            <input
              type="text"
              placeholder="Buscar por nome, cupom, autor..."
              value={search}
              onChange={(e) => handleFilterChange(() => setSearch(e.target.value))}
              className="
                h-10 w-full rounded-xl border border-slate-200 bg-white pl-10 pr-3 text-sm text-slate-800
                outline-none transition-colors placeholder:text-slate-400
                focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10
              "
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {STATUS_FILTER_OPTIONS.map((opt) => {
              const isActive = statusFilter === opt.value;

              const classes =
                opt.value === 'all'
                  ? isActive
                    ? 'border-slate-800 bg-slate-800 text-white'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                  : isActive
                    ? STATUS_CHIP_CLASSES[opt.value]
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50';

              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() =>
                    handleFilterChange(() =>
                      setStatusFilter(opt.value as HistoryStatus | 'all'),
                    )
                  }
                  className={`
                    h-8 rounded-full border px-3 text-xs transition-colors
                    ${isActive ? 'font-bold' : 'font-medium'}
                    ${classes}
                  `}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>

          <div className="hidden h-8 w-px bg-slate-200 xl:block" />

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:flex xl:items-center">
            <label className="relative min-w-[180px]">
              <span className="mb-1 block text-[0.7rem] font-semibold uppercase tracking-wide text-slate-500">
                Template
              </span>

              <select
                value={templateFilter}
                onChange={(e) => handleFilterChange(() => setTemplateFilter(e.target.value))}
                className="
                  h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-800
                  outline-none transition-colors
                  focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10
                "
              >
                <option value="all">Todos os templates</option>
                {allTemplates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="relative min-w-[160px]">
              <span className="mb-1 block text-[0.7rem] font-semibold uppercase tracking-wide text-slate-500">
                Responsável
              </span>

              <select
                value={authorFilter}
                onChange={(e) => handleFilterChange(() => setAuthorFilter(e.target.value))}
                className="
                  h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-800
                  outline-none transition-colors
                  focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10
                "
              >
                <option value="all">Todos</option>
                {allAuthors.map((author) => (
                  <option key={author} value={author}>
                    {author}
                  </option>
                ))}
              </select>
            </label>
            <label className="relative min-w-[180px]">
              <span className="mb-1 block text-[0.7rem] font-semibold uppercase tracking-wide text-slate-500">
                Status Pipefy
              </span>

              <select
                value={pipefyFilter}
                onChange={(e) => handleFilterChange(() => setPipefyFilter(e.target.value as PipefyStatus | 'all'))}
                className="
                  h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-800
                  outline-none transition-colors
                  focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10
                "
              >
                {PIPEFY_FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={handleClearFilters}
              className="
                inline-flex h-9 items-center justify-center gap-1.5 rounded-full border border-slate-200
                bg-white px-3 text-xs font-semibold text-slate-600 transition-colors
                hover:bg-slate-50 hover:text-slate-900
              "
            >
              Limpar filtros
              <X size={13} />
            </button>
          )}

          <div className="flex items-center rounded-xl border border-slate-200 bg-slate-50 p-1 xl:ml-auto">
            <button
              type="button"
              title="Visualização em grade"
              aria-label="Visualização em grade"
              onClick={() => {
                setViewMode('grid');
                setPage(1);
              }}
              className={`
                flex h-8 w-9 items-center justify-center rounded-lg transition-colors
                ${viewMode === 'grid'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-800'
                }
              `}
            >
              <Grid3X3 size={17} />
            </button>

            <button
              type="button"
              title="Visualização em lista"
              aria-label="Visualização em lista"
              onClick={() => {
                setViewMode('list');
                setPage(1);
              }}
              className={`
                flex h-8 w-9 items-center justify-center rounded-lg transition-colors
                ${viewMode === 'list'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-800'
                }
              `}
            >
              <List size={18} />
            </button>
          </div>
        </section>

        <section className="mb-4 flex flex-wrap items-center gap-2 text-sm text-slate-500">
          <div className="relative">
            <Filter size={18} className="text-slate-500" />

            {activeFilterCount > 0 && (
              <span
                className="
                  absolute -right-2 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full
                  bg-blue-600 px-1 text-[0.6rem] font-bold leading-none text-white
                "
              >
                {activeFilterCount}
              </span>
            )}
          </div>

          <span>
            {filtered.length === records.length
              ? `${filtered.length} registros`
              : `${filtered.length} de ${records.length} registros`}
          </span>

          {filtered.length > 0 && (
            <span className="text-slate-400">
              · página {safePage} de {pageCount}
            </span>
          )}
        </section>

        {selectedRecords.length > 0 && (
          <section className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 shadow-sm">
            <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm">
              <FileSpreadsheet size={18} className="shrink-0 text-blue-700" />

              <span className="font-semibold text-blue-900">
                {selectedRecords.length} {selectedRecords.length === 1 ? 'selecionado' : 'selecionados'}
              </span>

              <span className="rounded-full bg-white px-2.5 py-1 text-xs font-bold text-blue-700 ring-1 ring-blue-200">
                {selectedTemplateName}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleClearSelection}
                className="
                  inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-blue-200
                  bg-white px-3 text-xs font-semibold text-blue-700 transition-colors
                  hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500/30
                "
              >
                Limpar seleção
                <X size={13} />
              </button>
              {canManagePipefy && (
                <Tooltip title={selectedPipefyActionTitle} arrow>
                  <span className="inline-flex">
                    <button
                      type="button"
                      disabled={selectedPipefyActionDisabled}
                      onClick={() => {
                        if (selectedPipefyActionKind === 'cancel') setPipefyCancelTargets(selectedRecords);
                        else if (selectedPipefyActionKind === 'send') setPipefySendTargets(selectedRecords);
                      }}
                      className={`
                        inline-flex h-9 items-center justify-center gap-1.5 rounded-lg px-3
                        text-xs font-bold text-white transition-colors
                        disabled:cursor-not-allowed disabled:opacity-45
                        focus:outline-none focus:ring-2 focus:ring-blue-500/30
                        ${selectedPipefyActionKind === 'cancel'
                          ? 'bg-rose-700 hover:bg-rose-800'
                          : 'bg-blue-700 hover:bg-blue-800'}
                      `}
                    >
                      {selectedPipefyActionKind === 'pending'
                        ? <LoaderCircle size={15} className="animate-spin" />
                        : selectedPipefyActionKind === 'cancel'
                          || selectedRecords.every((record) => getPipefyStatus(record) === 'cancelled')
                          ? <Ban size={15} />
                          : selectedPipefyActionKind === 'locked'
                            ? <AlertCircle size={15} />
                            : <CloudUpload size={15} />}
                      {selectedPipefyActionLabel}
                    </button>
                  </span>
                </Tooltip>
              )}

              <button
                type="button"
                onClick={handleExportSelectedXlsx}
                className="
                  inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-blue-700 px-3
                  text-xs font-bold text-white transition-colors hover:bg-blue-800
                  focus:outline-none focus:ring-2 focus:ring-blue-500/30
                "
              >
                <FileSpreadsheet size={15} />
                Exportar XLSX
              </button>
            </div>
          </section>
        )}

        {filtered.length === 0 ? (
          <section
            className="
              rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center shadow-sm
            "
          >
            <Search size={42} className="mx-auto mb-3 text-slate-300" />

            <h2 className="text-base font-semibold text-slate-600">
              Nenhum resultado encontrado
            </h2>

            <p className="mt-1 text-sm text-slate-400">
              Tente ajustar os filtros ou o termo de busca.
            </p>
          </section>
        ) : viewMode === 'grid' ? (
          <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
            {paginated.map((record) => {
              const isSelected = selectedRecordIdSet.has(String(record.id));
              const selectionDisabled = isRecordSelectionDisabled(record);

              return (
                <HistoryCard
                  key={record.id}
                  record={record}
                  isSelected={isSelected}
                  selectionDisabled={selectionDisabled}
                  onClick={() => handleOpenRecord(record)}
                  onExportXlsx={() => handleExportRecordXlsx(record)}
                  onReuse={() => handleReuseRecord(record)}
                  onToggleSelect={() => handleToggleRecordSelection(record)}
                  onSendPipefy={() => setPipefySendTargets([record])}
                  onCancelPipefy={() => setPipefyCancelTargets([record])}
                  canManagePipefy={canManagePipefy}
                  pipefyBusy={pipefyBusyIds.has(String(record.id)) || ['queued', 'processing'].includes(getPipefyStatus(record))}
                />
              );
            })}
          </section>
        ) : (
          <section className="flex flex-col gap-3">
            <HistoryListHeader />

            {paginated.map((record) => {
              const isSelected = selectedRecordIdSet.has(String(record.id));
              const selectionDisabled = isRecordSelectionDisabled(record);

              return (
                <HistoryListRow
                  key={record.id}
                  record={record}
                  isSelected={isSelected}
                  selectionDisabled={selectionDisabled}
                  onClick={() => handleOpenRecord(record)}
                  onExportXlsx={() => handleExportRecordXlsx(record)}
                  onReuse={() => handleReuseRecord(record)}
                  onToggleSelect={() => handleToggleRecordSelection(record)}
                  onSendPipefy={() => setPipefySendTargets([record])}
                  onCancelPipefy={() => setPipefyCancelTargets([record])}
                  canManagePipefy={canManagePipefy}
                  pipefyBusy={pipefyBusyIds.has(String(record.id)) || ['queued', 'processing'].includes(getPipefyStatus(record))}
                />
              );
            })}
          </section>
        )}

        {pageCount > 1 && (
          <div className="mt-8 flex justify-center">
            <Pagination
              page={safePage}
              pageCount={pageCount}
              onChange={(nextPage) => {
                setPage(nextPage);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            />
          </div>
        )}
      </main>
      {canManagePipefy && pipefySendTargets.length > 0 && (
        <div
          className="fixed inset-0 z-[1070] flex items-center justify-center bg-slate-950/45 p-4"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="pipefy-send-title"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !pipefySendBusy) setPipefySendTargets([]);
          }}
        >
          <div className="w-full max-w-lg rounded-xl border border-blue-200 bg-white shadow-2xl">
            <div className="flex items-start gap-3 border-b border-blue-100 px-5 py-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-700">
                <CloudUpload size={20} />
              </span>
              <div>
                <h2 id="pipefy-send-title" className="text-base font-bold text-slate-900">
                  Enviar ao Pipefy?
                </h2>
                <p className="mt-1 text-sm leading-relaxed text-slate-600">
                  A promocao sera adicionada a fila e acompanhada ate a conclusao da importacao.
                </p>
              </div>
            </div>

            <div className="px-5 py-4 text-sm text-slate-600">
              {pipefySendTargets.length === 1
                ? 'Esta promocao sera enviada ao Pipefy.'
                : `${pipefySendTargets.length} promocoes selecionadas serao enviadas ao Pipefy.`}
              <p className="mt-2 rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-800 ring-1 ring-blue-200">
                O botao ficara bloqueado durante o processamento e mudara para Cancelar no Pipefy quando o envio terminar.
              </p>
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3">
              <button
                type="button"
                disabled={pipefySendBusy}
                onClick={() => setPipefySendTargets([])}
                className="h-9 rounded-lg px-3 text-sm font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-45"
              >
                Voltar
              </button>
              <button
                type="button"
                disabled={pipefySendBusy}
                onClick={() => void handleSendRecordsToPipefy(pipefySendTargets)}
                className="inline-flex h-9 items-center gap-2 rounded-lg bg-blue-700 px-4 text-sm font-bold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-45"
              >
                {pipefySendBusy ? <LoaderCircle size={15} className="animate-spin" /> : <CloudUpload size={15} />}
                Confirmar envio
              </button>
            </div>
          </div>
        </div>
      )}
      {canManagePipefy && pipefyCancelTargets.length > 0 && (
        <div
          className="fixed inset-0 z-[1070] flex items-center justify-center bg-slate-950/45 p-4"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="pipefy-cancel-title"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !pipefyCancelBusy) setPipefyCancelTargets([]);
          }}
        >
          <div className="w-full max-w-lg rounded-xl border border-rose-200 bg-white shadow-2xl">
            <div className="flex items-start gap-3 border-b border-rose-100 px-5 py-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-rose-100 text-rose-700">
                <Ban size={20} />
              </span>
              <div>
                <h2 id="pipefy-cancel-title" className="text-base font-bold text-slate-900">
                  Cancelar no Pipefy?
                </h2>
                <p className="mt-1 text-sm leading-relaxed text-slate-600">
                  Todos os cards gerados pela mesma importacao serao movidos para
                  <strong> Canceladas / Rejeitadas</strong>. Nenhum card sera excluido.
                </p>
              </div>
            </div>

            <div className="px-5 py-4 text-sm text-slate-600">
              {pipefyCancelTargets.length === 1
                ? 'Esta promocao sera cancelada no Pipefy.'
                : `${pipefyCancelTargets.length} promocoes selecionadas serao canceladas no Pipefy.`}
              <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 ring-1 ring-amber-200">
                Quando uma planilha gerou varios cards, o cancelamento inclui o conjunto completo daquela importacao.
                Depois de confirmado, este briefing nao podera ser enviado nem cancelado novamente.
              </p>
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3">
              <button
                type="button"
                disabled={pipefyCancelBusy}
                onClick={() => setPipefyCancelTargets([])}
                className="h-9 rounded-lg px-3 text-sm font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-45"
              >
                Voltar
              </button>
              <button
                type="button"
                disabled={pipefyCancelBusy}
                onClick={() => void handleCancelPipefy()}
                className="inline-flex h-9 items-center gap-2 rounded-lg bg-rose-700 px-4 text-sm font-bold text-white hover:bg-rose-800 disabled:cursor-not-allowed disabled:opacity-45"
              >
                {pipefyCancelBusy ? <LoaderCircle size={15} className="animate-spin" /> : <Ban size={15} />}
                Confirmar cancelamento
              </button>
            </div>
          </div>
        </div>
      )}

      {canCancelPipefyCards && (
        <CancelPipefyCardsModal
          open={cancelCardsModalOpen}
          onClose={() => setCancelCardsModalOpen(false)}
          onCompleted={(result) => {
            const moved = result.moved.length;
            const alreadyCancelled = result.alreadyCancelled.length;
            const failed = result.failed.length;
            setPipefyNotice({
              kind: failed > 0 ? 'error' : 'success',
              message: moved + ' ' + (moved === 1 ? 'card movido' : 'cards movidos')
                + ', ' + alreadyCancelled + ' já cancelados e ' + failed + ' falhas.',
            });
          }}
        />
      )}

      <HistoryDetailDrawer
        record={selectedRecord}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onDeleted={handleRecordDeleted}
        onReuse={handleReuseRecord}
      />
    </div>
  );
}
